import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import DadosEngenheiroPanel from '../components/DadosEngenheiroPanel';
import ConversasPanel from '../components/ConversasPanel';
import FotosLocalPanel from '../components/FotosLocalPanel';
import FotosInstalacaoPanel from '../components/FotosInstalacaoPanel';
import EnviarVendedorModal from '../components/EnviarVendedorModal';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (d) => {
  if (!d) return null;
  // Se já é datetime completo (contém 'T'), usa direto; se é só data, adiciona meio-dia pra evitar bug de UTC
  const str = typeof d === 'string' && !d.includes('T') ? d + 'T12:00:00' : d;
  const date = new Date(str);
  return isNaN(date) ? '—' : date.toLocaleDateString('pt-BR');
};
const fmtVal = (v) =>
  v != null ? `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : null;
const fmtDateTime = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (isNaN(date)) return null;
  return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const _COR_VERDE    = new Set(['fechado','finalizado','vistoria_feita','instalacao_concluida','pago','concluido']);
const _COR_VERMELHO = new Set(['aguardando_vendedor','aguardando_engenheiro','aguardando_instalador',
                                'aguardando_pagamento','aguardando_cemig','aguardando',
                                'perdido','cancelado']);

function statusCor(statusKey) {
  if (!statusKey) return '#1e3a5f';
  if (_COR_VERDE.has(statusKey))    return '#4ade80';
  if (_COR_VERMELHO.has(statusKey)) return '#f87171';
  return '#f6c90e';
}

function stCorCircle(s) {
  if (!s) return '#1e2d45';
  if (_COR_VERDE.has(s))    return '#4ade80';
  if (_COR_VERMELHO.has(s)) return '#f87171';
  return '#f6c90e';
}

// ── Estilos ───────────────────────────────────────────────────────────────────
const S = {
  page:      { maxWidth: 860, margin: '0 auto', padding: '0 0 48px 0' },
  back:      { display:'flex', alignItems:'center', gap:8, color:'#60a5fa', fontSize:13,
               cursor:'pointer', marginBottom:20, width:'fit-content',
               background:'none', border:'none', padding:0 },
  heading:   { fontSize:24, fontWeight:800, color:'#f6c90e', marginBottom:2 },
  sub:       { fontSize:13, color:'#475569', marginBottom:24 },

  card:      { background:'#0d1424', border:'1px solid #1a2035', borderRadius:14,
               padding:'20px 24px', marginBottom:20 },
  cardTitle: { fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase',
               letterSpacing:'0.07em', marginBottom:14 },

  grid2:     { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px 24px' },
  lbl:       { fontSize:11, color:'#475569', marginBottom:2 },
  val:       { fontSize:13, color:'#e2e8f0', fontWeight:500 },

  // Timeline
  timeline:  { position:'relative', paddingLeft:28 },
  tlLine:    { position:'absolute', left:9, top:12, bottom:0,
               width:2, background:'#1a2035', borderRadius:2 },
  tlItem:    { position:'relative', marginBottom:22 },
  tlDot:     (c) => ({ position:'absolute', left:-28, top:4, width:12, height:12,
               borderRadius:'50%', background:c, border:'2px solid #0d1424',
               boxShadow:`0 0 0 2px ${c}44` }),
  tlHead:    { display:'flex', alignItems:'center', gap:10, marginBottom:8 },
  tlTitle:   { fontSize:14, fontWeight:700, color:'#e2e8f0' },
  tlBadge:   (c) => ({ fontSize:11, fontWeight:700, color:c, background:`${c}18`,
               border:`1px solid ${c}40`, borderRadius:20, padding:'2px 10px' }),
  tlBody:    { background:'#111827', borderRadius:10, padding:'12px 16px',
               border:'1px solid #1a2035' },
  tlRow:     { marginBottom:5 },
  tlLbl:     { fontSize:11, color:'#475569' },
  tlVal:     { fontSize:13, color:'#cbd5e1', fontWeight:500 },
  tlObs:     { fontSize:12, color:'#64748b', marginTop:8, fontStyle:'italic',
               borderTop:'1px solid #1a2035', paddingTop:8 },

  fileLink:  (hasFile) => ({
               display:'inline-flex', alignItems:'center', gap:6,
               fontSize:12, fontWeight:600,
               color: hasFile ? '#60a5fa' : '#475569',
               background: hasFile ? 'rgba(96,165,250,0.1)' : 'rgba(71,85,105,0.1)',
               border: `1px solid ${hasFile ? 'rgba(96,165,250,0.3)' : 'rgba(71,85,105,0.2)'}`,
               borderRadius:7, padding:'5px 12px', textDecoration:'none',
               cursor: hasFile ? 'pointer' : 'default',
             }),
};

// ── Sub-componentes ───────────────────────────────────────────────────────────
function Dado({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div>
      <div style={S.lbl}>{label}</div>
      <div style={S.val}>{value}</div>
    </div>
  );
}

function TRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div style={S.tlRow}>
      <span style={S.tlLbl}>{label}: </span>
      <span style={S.tlVal}>{value}</span>
    </div>
  );
}

function Etapa({ icon, title, statusKey, statusDisplay, cor: corOverride, children }) {
  const cor = corOverride || statusCor(statusKey);
  return (
    <div style={S.tlItem}>
      <div style={S.tlDot(cor)} />
      <div style={S.tlHead}>
        <span style={{ fontSize:16 }}>{icon}</span>
        <span style={S.tlTitle}>{title}</span>
        {statusDisplay && <span style={S.tlBadge(cor)}>{statusDisplay}</span>}
      </div>
      <div style={S.tlBody}>{children}</div>
    </div>
  );
}

function PipelineCard({ lead }) {
  const etapas = [
    { label: 'V', title: 'Vendedor',    status: lead.status },
    { label: 'E', title: 'Engenheiro',  status: lead.engenheiro?.status },
    { label: 'P', title: 'Pós Venda',   status: lead.pos_venda?.status },
    { label: 'I', title: 'Instalador',  status: lead.instalador?.status },
    { label: 'F', title: 'Financeiro',  status: lead.financeiro?.status },
  ];

  const STATUS_DISPLAY = {
    // lead
    pre_venda:'Pré Venda', aguardando_vendedor:'Aguardando', visita:'Visita',
    proposta:'Proposta', fechado:'Fechado', perdido:'Perdido', em_espera:'Aguardando',
    // engenheiro
    aguardando_engenheiro:'Aguardando', realizando_planejamento:'Planejamento',
    aguardando_cemig:'Ag. CEMIG', documentacao_aprovada:'Doc. Aprovada', vistoria_feita:'Vistoria OK',
    // pos venda
    aguardando:'Aguardando', na_transportadora:'Transportadora', processo_entrega:'Processo de Entrega', entregue:'Entregue', instalando:'Instalando', finalizado:'Finalizado',
    // instalador
    aguardando_instalador:'Aguardando', instalacao_programada:'Programada',
    instalacao_concluida:'Concluída',
    // financeiro
    aguardando_pagamento:'Ag. Pagamento', pago:'Pago',
  };

  return (
    <div style={{ ...S.card, marginBottom:20 }}>
      <div style={S.cardTitle}>Pipeline</div>
      <div style={{ display:'flex', gap:12 }}>
        {etapas.map(({ label, title, status }) => {
          const c = stCorCircle(status);
          const textColor = status ? '#0d1424' : '#334155';
          return (
            <div key={label} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
              <div style={{
                width:32, height:32, borderRadius:'50%',
                background: c,
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:12, fontWeight:800, color: textColor,
                boxShadow: status ? `0 0 0 3px ${c}33` : 'none',
              }}>{label}</div>
              <div style={{ fontSize:10, color:'#475569', textAlign:'center', maxWidth:56 }}>{title}</div>
              {status && (
                <div style={{ fontSize:9, color: c, fontWeight:600, textAlign:'center', maxWidth:60 }}>
                  {STATUS_DISPLAY[status] || status}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
function FotosInstalModal({ fotos, onClose }) {
  const [idx, setIdx] = React.useState(0);
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.88)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ position:'relative', maxWidth:'90vw' }}>
        <button onClick={onClose} style={{ position:'absolute', top:-38, right:0, background:'none', border:'none', color:'#fff', fontSize:26, cursor:'pointer', lineHeight:1 }}>✕</button>
        <img src={fotos[idx].url} alt={fotos[idx].nome}
          style={{ maxWidth:'82vw', maxHeight:'80vh', borderRadius:10, objectFit:'contain', display:'block' }} />
        {fotos.length > 1 && (
          <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:14, marginTop:12 }}>
            <button onClick={() => setIdx(i => (i - 1 + fotos.length) % fotos.length)}
              style={{ background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', borderRadius:6, padding:'6px 16px', cursor:'pointer', fontSize:18 }}>‹</button>
            <span style={{ color:'#94a3b8', fontSize:13 }}>{idx + 1} / {fotos.length}</span>
            <button onClick={() => setIdx(i => (i + 1) % fotos.length)}
              style={{ background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', borderRadius:6, padding:'6px 16px', cursor:'pointer', fontSize:18 }}>›</button>
          </div>
        )}
        <div style={{ textAlign:'center', color:'#64748b', fontSize:12, marginTop:8 }}>{fotos[idx].nome}</div>
      </div>
    </div>
  );
}

export default function JornadaCliente() {
  const { clienteId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fotosModal, setFotosModal] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [clienteForm, setClienteForm] = useState({});
  const [savingCliente, setSavingCliente] = useState(false);
  const [gerandoPosObra, setGerandoPosObra] = useState(false);
  const [showEnviarVendedor, setShowEnviarVendedor] = useState(false);
  const { isChefe, isVendedor, isPreVenda, isEngenheiro, isPosVenda, isGerenteComercial, isInstalador } = useAuth();

  const reloadJornada = () => api.get(`/jornada/${clienteId}/`).then(r => {
    setData(r.data);
    const c = r.data.cliente;
    setClienteForm({ nome: c.nome||'', telefone: c.telefone||'', email: c.email||'', endereco: c.endereco||'', cidade: c.cidade||'', estado: c.estado||'', cpf_doc: c.cpf_doc||'', rg_doc: c.rg_doc||'', consumo_mensal: c.consumo_mensal ? String(c.consumo_mensal) : '' });
  });

  useEffect(() => {
    reloadJornada().catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [clienteId]);

  if (loading) return (
    <div style={{ color:'#64748b', fontSize:14, padding:32 }}>Carregando…</div>
  );
  if (!data) return (
    <div style={{ color:'#f87171', fontSize:14, padding:32 }}>Cliente não encontrado.</div>
  );

  const { cliente, leads, arquivos_cliente = [] } = data;

  // Lead mais recente — usado para permissões e para o envio ao vendedor
  const ultimoLead = leads.length > 0 ? leads[leads.length - 1] : null;
  const ultimoStatus = ultimoLead ? ultimoLead.status : null;
  const podeEditar = (isChefe() || isVendedor() || isGerenteComercial() || isPreVenda()) && ultimoStatus !== 'fechado';
  // Envio ao vendedor: só quem o backend autoriza a chamar pre_venda_lead_detail
  const podeEnviarVendedor = (isChefe() || isPreVenda()) && !!ultimoLead && ultimoStatus !== 'fechado';

  const saveClienteData = async () => {
    setSavingCliente(true);
    try {
      await api.post(`/documentos/salvar-cpf-rg/${clienteId}/`, clienteForm);
      await reloadJornada();
      setEditOpen(false);
    } catch (e) {
      alert('Erro ao salvar: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    } finally { setSavingCliente(false); }
  };

  // Agrupa arquivos por categoria
  const ARQ_LABELS = {
    foto_padrao:'Foto Padrão', foto_disjuntor:'Foto Disjuntor', foto_medidor:'Foto Medidor',
    foto_estrutura_telhado:'Foto Estrutura Telhado', foto_frente_imovel:'Foto Frente Imóvel',
    foto_vistoria_tecnica:'Foto Vistoria Técnica', conta_cemig:'Conta CEMIG',
    prova_venda:'Prova de Venda', foto_instalacao:'Foto Instalação',
  };
  const FOTOS_CATS = ['foto_padrao','foto_disjuntor','foto_medidor','foto_estrutura_telhado','foto_frente_imovel','foto_vistoria_tecnica'];
  const DOCS_CATS  = ['conta_cemig'];

  return (
    <div style={S.page}>
      {fotosModal && (
        <FotosInstalModal fotos={fotosModal} onClose={() => setFotosModal(null)} />
      )}
      <button style={S.back} onClick={() => navigate(-1)}>← Voltar</button>

      <div style={S.heading}>{cliente.nome}</div>
      <div style={S.sub}>Jornada completa do cliente no sistema</div>

      {/* ── Dados do Cliente ────────────────────────────────────────────── */}
      <div style={S.card}>
        <div style={S.cardTitle}>Dados do Cliente</div>
        <div style={{ ...S.grid2, marginBottom: 0 }}>
          <Dado label="Telefone"     value={cliente.telefone} />
          <Dado label="Cidade / UF"  value={[cliente.cidade, cliente.estado].filter(Boolean).join(' / ')} />
          <Dado label="E-mail"       value={cliente.email} />
          <Dado label="Vendedor"     value={cliente.vendedor} />
          <Dado label="CPF / CNPJ"   value={cliente.cpf_cnpj} />
          <Dado label="CPF (doc)"    value={cliente.cpf_doc} />
          <Dado label="RG"           value={cliente.rg_doc} />
        </div>
        {cliente.endereco && (
          <div style={{ marginTop:10 }}>
            <div style={S.lbl}>Endereço</div>
            <div style={S.val}>{cliente.endereco}</div>
          </div>
        )}
        {cliente.consumo_mensal && (
          <div style={{ marginTop:8 }}>
            <Dado label="Consumo Mensal" value={`R$ ${Number(cliente.consumo_mensal).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} />
          </div>
        )}
        {(() => {
          const ultimoLead = leads.length > 0 ? leads[leads.length - 1] : null;
          if (!ultimoLead?.kit_geracao && !ultimoLead?.kit_equipamento) return null;
          return (
            <div style={{ marginTop: 12, background: 'rgba(246,201,14,0.06)', border: '1px solid rgba(246,201,14,0.2)', borderRadius: 9, padding: '10px 14px' }}>
              <div style={{ fontSize: 11, color: '#f6c90e', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>📦 Kit Solar</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 16px' }}>
                {ultimoLead.kit_equipamento && (
                  <div><div style={S.lbl}>Inversor / Equipamento</div><div style={S.val}>{ultimoLead.kit_equipamento}</div></div>
                )}
                <div>
                  <div style={S.lbl}>Marca</div>
                  <div style={S.val}>{ultimoLead.kit_marca || '—'}</div>
                </div>
                {ultimoLead.kit_geracao && (
                  <div><div style={S.lbl}>Geração estimada</div><div style={{ ...S.val, color: '#f6c90e', fontWeight: 700 }}>{ultimoLead.kit_geracao} kWh/mês</div></div>
                )}
                {ultimoLead.kit_quantidade && (
                  <div><div style={S.lbl}>Quantidade de placas</div><div style={S.val}>{ultimoLead.kit_quantidade} unidades</div></div>
                )}
                {ultimoLead.kit_placas_w && (
                  <div><div style={S.lbl}>Potência por placa</div><div style={S.val}>{ultimoLead.kit_placas_w} Wp</div></div>
                )}
              </div>
            </div>
          );
        })()}
        {cliente.como_foi_conversa && (
          <div style={{ marginTop:10, fontSize:12, color:'#64748b', fontStyle:'italic' }}>
            "{cliente.como_foi_conversa}"
          </div>
        )}
        <div style={{ marginTop:10, fontSize:11, color:'#334155' }}>
          Cadastrado em {fmt(cliente.criado_em)}
        </div>
        {podeEditar && (
          <div style={{ marginTop:12 }}>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
              <button
                onClick={() => setEditOpen(o => !o)}
                style={{ fontSize:12, fontWeight:600, color:'#f6c90e', background:'rgba(246,201,14,0.1)', border:'1px solid rgba(246,201,14,0.3)', borderRadius:7, padding:'6px 14px', cursor:'pointer' }}>
                {editOpen ? '▲ Fechar edição' : '✏️ Editar dados'}
              </button>
              {podeEnviarVendedor && (
                <button
                  onClick={() => setShowEnviarVendedor(true)}
                  style={{ fontSize:12, fontWeight:600, color:'#4ade80', background:'rgba(74,222,128,0.1)', border:'1px solid rgba(74,222,128,0.3)', borderRadius:7, padding:'6px 14px', cursor:'pointer' }}>
                  📤 Enviar para Vendedor
                </button>
              )}
            </div>
            {editOpen && (
              <div style={{ marginTop:12 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 12px' }}>
                  {[
                    ['Nome', 'nome', 'text'],
                    ['Telefone', 'telefone', 'text'],
                    ['E-mail', 'email', 'email'],
                    ['Consumo Mensal (R$)', 'consumo_mensal', 'number'],
                    ['CPF / CNPJ', 'cpf_doc', 'text'],
                    ['RG', 'rg_doc', 'text'],
                  ].map(([label, field, type]) => (
                    <div key={field}>
                      <div style={{ fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:1, marginBottom:4, marginTop:10 }}>{label}</div>
                      <input
                        type={type}
                        value={clienteForm[field] || ''}
                        onChange={e => setClienteForm(p => ({...p, [field]: e.target.value}))}
                        style={{ width:'100%', padding:'8px 10px', borderRadius:7, background:'#0e1420', border:'1px solid #1a2035', color:'#e2e8f0', fontSize:13, outline:'none', boxSizing:'border-box' }}
                      />
                    </div>
                  ))}
                  <div style={{ gridColumn:'1/-1' }}>
                    <div style={{ fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:1, marginBottom:4, marginTop:10 }}>Endereço</div>
                    <input value={clienteForm.endereco || ''} onChange={e => setClienteForm(p=>({...p,endereco:e.target.value}))} style={{ width:'100%', padding:'8px 10px', borderRadius:7, background:'#0e1420', border:'1px solid #1a2035', color:'#e2e8f0', fontSize:13, outline:'none', boxSizing:'border-box' }} />
                  </div>
                  <div>
                    <div style={{ fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:1, marginBottom:4, marginTop:10 }}>Cidade</div>
                    <input value={clienteForm.cidade || ''} onChange={e => setClienteForm(p=>({...p,cidade:e.target.value}))} style={{ width:'100%', padding:'8px 10px', borderRadius:7, background:'#0e1420', border:'1px solid #1a2035', color:'#e2e8f0', fontSize:13, outline:'none', boxSizing:'border-box' }} />
                  </div>
                  <div>
                    <div style={{ fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:1, marginBottom:4, marginTop:10 }}>Estado</div>
                    <input value={clienteForm.estado || ''} onChange={e => setClienteForm(p=>({...p,estado:e.target.value}))} maxLength={2} placeholder="MG" style={{ width:'100%', padding:'8px 10px', borderRadius:7, background:'#0e1420', border:'1px solid #1a2035', color:'#e2e8f0', fontSize:13, outline:'none', boxSizing:'border-box' }} />
                  </div>
                </div>
                <button
                  onClick={saveClienteData} disabled={savingCliente}
                  style={{ marginTop:14, padding:'9px 20px', borderRadius:8, background:'rgba(74,222,128,0.15)', border:'1px solid rgba(74,222,128,0.3)', color:'#4ade80', fontSize:13, fontWeight:700, cursor:'pointer', opacity: savingCliente ? 0.6 : 1 }}>
                  {savingCliente ? 'Salvando...' : '💾 Salvar dados do cliente'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Fotos do Local + Instalação (nível cliente) ─────────────── */}
      {leads.length > 0 && (() => {
        const ul = leads[leads.length - 1];
        return (
          <div style={{ marginBottom: 20 }}>
            <FotosLocalPanel leadId={ul.id} readonly={!(isChefe() || isGerenteComercial() || isInstalador())} />
            {ul.instalador && (
              <div style={{ marginTop: 12 }}>
                <FotosInstalacaoPanel leadId={ul.id} readonly={!(isChefe() || isGerenteComercial() || isInstalador())} />
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Documentos do Cliente ───────────────────────────── */}
      {(() => {
        const docs  = arquivos_cliente.filter(a => DOCS_CATS.includes(a.categoria));
        const procuracaoUrl = [...leads].reverse().find(l => l.cemig_assinado_url)?.cemig_assinado_url;
        const showEngenheiro = isChefe() || isEngenheiro() || isPosVenda() || isGerenteComercial();
        if (!docs.length && !procuracaoUrl && !showEngenheiro) return null;
        return (
          <div style={S.card}>
            <div style={S.cardTitle}>Documentos do Cliente</div>
            {(docs.length > 0 || procuracaoUrl) && (
              <>
                <div style={{ fontSize:11, color:'#60a5fa', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:8 }}>Documentos</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                  {docs.map(f => (
                    <a key={f.id} href={f.url} target="_blank" rel="noopener noreferrer"
                      style={{ ...S.fileLink(true), justifyContent:'flex-start' }}>
                      📄 {ARQ_LABELS[f.categoria] || f.categoria}
                    </a>
                  ))}
                  {procuracaoUrl && (
                    <a href={procuracaoUrl} target="_blank" rel="noopener noreferrer"
                      style={{ ...S.fileLink(true), justifyContent:'flex-start' }}>
                      📄 Ver Procuração
                    </a>
                  )}
                </div>
              </>
            )}
            {/* Dados do Engenheiro — só chefe, engenheiro, pós venda e gerente comercial */}
            {(isChefe() || isEngenheiro() || isPosVenda() || isGerenteComercial()) && (
              <div style={{ marginTop:14 }}>
                <DadosEngenheiroPanel clienteId={cliente.id} readonly />
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Leads ───────────────────────────────────────────────────────── */}
      {leads.length === 0 && (
        <div style={{ color:'#475569', fontSize:13 }}>Nenhum lead cadastrado para este cliente.</div>
      )}

      {leads.map((lead, idx) => (
        <div key={lead.id}>
          {leads.length > 1 && (
            <div style={{ fontSize:12, color:'#334155', fontWeight:700,
                          textTransform:'uppercase', letterSpacing:'0.06em',
                          marginBottom:12, marginTop: idx > 0 ? 24 : 0 }}>
              Lead #{lead.id} — {fmt(lead.criado_em) || ''}
            </div>
          )}

          {/* ── Pipeline V E P I F ──────────────────────────────────────── */}
          <PipelineCard lead={lead} />

          <div style={S.card}>
            <div style={S.cardTitle}>Jornada do Cliente</div>
            <div style={S.timeline}>
              <div style={S.tlLine} />

              {/* ── 1. Pré Venda ──────────────────────────────────────── */}
              <Etapa icon="📋" title="Pré Venda" cor="#4ade80">
                <TRow label="Cadastrado em" value={fmt(lead.criado_em)} />
                {lead.observacoes && (
                  <div style={S.tlObs}>"{lead.observacoes}"</div>
                )}
                {lead.followups_pre_venda?.length > 0 && (
                  <div style={{ marginTop:10 }}>
                    <div style={{ fontSize:11, color:'#fb923c', fontWeight:700, textTransform:'uppercase', letterSpacing:0.5, marginBottom:6 }}>
                      Follow Ups ({lead.followups_pre_venda.length})
                    </div>
                    {lead.followups_pre_venda.map(fu => (
                      <div key={fu.id} style={{ background:'rgba(251,146,60,0.06)', border:'1px solid rgba(251,146,60,0.18)', borderRadius:7, padding:'8px 10px', marginBottom:6 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                          <span style={{ fontSize:11, color:'#fb923c', fontWeight:600 }}>
                            {fmt(fu.data_contato)}
                          </span>
                          <span style={{ fontSize:10, color:'#475569' }}>{fu.criado_por}</span>
                        </div>
                        <div style={{ fontSize:12, color:'#94a3b8', lineHeight:1.5 }}>{fu.nota}</div>
                      </div>
                    ))}
                  </div>
                )}
              </Etapa>

              {/* ── 2. Vendedor ───────────────────────────────────────── */}
              <Etapa
                icon="🤝" title="Vendedor"
                statusKey={lead.status}
                statusDisplay={lead.status_display}
              >
                <TRow label="Vendedor"           value={lead.vendedor} />
                <TRow label="Enviado para Vendedor em" value={fmtDateTime(lead.atribuido_vendedor_em)} />
                <TRow label="Prazo para Contato" value={fmtDateTime(lead.prazo_resposta_vendedor)} />
                <TRow label="Valor da Venda"     value={fmtVal(lead.valor_total_venda)} />
                <TRow label="Forma Pagamento"    value={lead.forma_pagamento} />
                <TRow label="Venda Fechada em"   value={fmt(lead.venda_fechada_em)} />
                <TRow label="Como foi a conversa" value={lead.como_foi_venda} />
                {/* Provas de venda */}
                {lead.prova_venda_url && (
                  <div style={{ marginTop:8 }}>
                    <a href={lead.prova_venda_url} target="_blank" rel="noopener noreferrer"
                      style={{ ...S.fileLink(true), display:'inline-flex' }}>📎 Prova de Venda</a>
                  </div>
                )}
                {(lead.arquivos_extras || []).filter(a => a.categoria === 'prova_venda').map(a => (
                  <div key={a.id} style={{ marginTop:4 }}>
                    <a href={a.url} target="_blank" rel="noopener noreferrer"
                      style={{ ...S.fileLink(true), display:'inline-flex' }}>📎 {a.nome}</a>
                  </div>
                ))}
              </Etapa>

              {/* ── 3. Engenheiro ─────────────────────────────────────── */}
              {lead.engenheiro && (
                <Etapa
                  icon="⚙️" title="Engenheiro"
                  statusKey={lead.engenheiro.status}
                  statusDisplay={lead.engenheiro.status_display}
                >
                  <TRow label="Responsável"  value={lead.engenheiro.responsavel || '—'} />
                  <TRow label="Início"       value={fmt(lead.engenheiro.data_inicio)} />
                  <TRow label="Previsão"     value={fmt(lead.engenheiro.data_prevista)} />
                  <TRow label="Conclusão"    value={fmt(lead.engenheiro.data_conclusao)} />
                  {lead.engenheiro.observacoes && (
                    <div style={S.tlObs}>"{lead.engenheiro.observacoes}"</div>
                  )}
                </Etapa>
              )}

              {/* ── 4. Pós Venda ──────────────────────────────────────── */}
              {lead.pos_venda && (
                <Etapa
                  icon="📦" title="Pós Venda"
                  statusKey={lead.pos_venda.status}
                  statusDisplay={lead.pos_venda.status_display}
                >
                  <TRow label="Responsável"  value={lead.pos_venda.responsavel || '—'} />
                  <TRow label="Data entrega transportadora" value={fmt(lead.pos_venda.data_entrega)} />
                  {lead.pos_venda.observacoes && (
                    <div style={S.tlObs}>"{lead.pos_venda.observacoes}"</div>
                  )}
                  {lead.pos_venda.id && (
                    <div style={{ marginTop:10 }}>
                      <button
                        disabled={gerandoPosObra}
                        onClick={async () => {
                          setGerandoPosObra(true);
                          try {
                            const resp = await api.get(`/pos-venda/${lead.pos_venda.id}/gerar-pos-obra/`, { responseType:'blob' });
                            const url  = window.URL.createObjectURL(new Blob([resp.data], { type:'application/pdf' }));
                            const a    = document.createElement('a');
                            a.href     = url;
                            a.download = `Pos obra - ${cliente.nome}.pdf`;
                            a.click();
                            window.URL.revokeObjectURL(url);
                          } catch (e) {
                            alert('Erro ao gerar documento: ' + (e.response?.data?.error || e.message || 'tente novamente'));
                          } finally {
                            setGerandoPosObra(false);
                          }
                        }}
                        style={{ color:'#f6c90e', fontSize:12, fontWeight:600, display:'inline-flex', alignItems:'center', gap:6,
                          background:'rgba(246,201,14,0.1)', border:'1px solid rgba(246,201,14,0.3)', borderRadius:6,
                          padding:'6px 12px', cursor: gerandoPosObra ? 'not-allowed' : 'pointer' }}>
                        📄 {gerandoPosObra ? 'Gerando...' : 'Ver Documento Pós Obra'}
                      </button>
                    </div>
                  )}
                </Etapa>
              )}

              {/* ── 5. Instalador ─────────────────────────────────────── */}
              {lead.instalador && (
                <Etapa
                  icon="🔧" title="Instalador"
                  statusKey={lead.instalador.status}
                  statusDisplay={lead.instalador.status_display}
                >
                  <TRow label="Instalador"    value={lead.instalador.instalador} />
                  <TRow label="Início"        value={fmt(lead.instalador.data_inicio)} />
                  <TRow label="Previsão"      value={fmt(lead.instalador.data_prevista)} />
                  <TRow label="Conclusão"     value={fmt(lead.instalador.data_conclusao)} />
                  {lead.instalador.observacoes && (
                    <div style={S.tlObs}>"{lead.instalador.observacoes}"</div>
                  )}
                  {(() => {
                    const fotosInstal = [
                      ...(lead.instalador.foto_url ? [{ url: lead.instalador.foto_url, nome: 'Foto da instalação' }] : []),
                      ...(lead.arquivos_extras || []).filter(a => a.categoria === 'foto_instalacao').map(a => ({ url: a.url, nome: a.nome || 'Foto instalação' })),
                    ];
                    if (fotosInstal.length === 0) return null;
                    return (
                      <div style={{ marginTop:10 }}>
                        <button onClick={() => setFotosModal(fotosInstal)}
                          style={{ color:'#a78bfa', fontSize:12, display:'inline-flex', alignItems:'center', gap:6, background:'rgba(167,139,250,0.1)', border:'1px solid rgba(167,139,250,0.3)', borderRadius:6, padding:'6px 12px', cursor:'pointer' }}>
                          🖼️ Ver fotos instalação ({fotosInstal.length})
                        </button>
                      </div>
                    );
                  })()}
                </Etapa>
              )}

              {/* ── 6. Financeiro ─────────────────────────────────────── */}
              {lead.financeiro && (
                <Etapa
                  icon="💰" title="Financeiro"
                  statusKey={lead.financeiro.status}
                  statusDisplay={lead.financeiro.status_display}
                >
                  <TRow label="Valor Total"      value={fmtVal(lead.financeiro.valor_total)} />
                  <TRow label="Forma Pagamento"  value={lead.financeiro.forma_pagamento} />
                  <TRow label="Status Pagamento" value={lead.financeiro.valor_pago ? '✅ Pago' : '⏳ Aguardando'} />
                  <TRow label="Data Pagamento"   value={fmt(lead.financeiro.data_pagamento)} />
                </Etapa>
              )}

            </div>
          </div>

          {/* ── Conversas (somente leitura) ─────────────────────────────── */}
          <div style={{ marginTop:16 }}>
            <ConversasPanel leadId={lead.id} readOnly={true} />
          </div>

        </div>
      ))}

      {showEnviarVendedor && ultimoLead && (
        <EnviarVendedorModal
          lead={{ id: ultimoLead.id, cliente_nome: cliente.nome, cliente_telefone: cliente.telefone, cliente_cidade: cliente.cidade }}
          onClose={() => setShowEnviarVendedor(false)}
          onSaved={reloadJornada}
        />
      )}
    </div>
  );
}
